/** @module csi/table/View */
define([
	"dojo/_base/declare",
	"underscore",
	"csi/def",
	"csi/def/Heading",
	"csi/def/View",
	"csi/reflection",
	"csi/table",
	"csi/util",
	"./Relationship"
], function(declare, _, def, Heading, View, reflection, table, util, Relationship) {

	return declare(View, {
		defaultImageSize: reflection.IMAGE_SIZE_SMALLEST,
		includeImageInDefaultCard: true,
		supportsImageLabels: true,

		/** @property {boolean} useMatrixCard - use the default matrix <TYPE>.Matrix card or not */
		useMatrixCard: true,

		constructor: function(xn) {
			this.m_bAllowActions = util.getBooleanProperty(xn, "AllowActions");
			this.m_bSort = util.getBooleanProperty(xn, "Sort", true);
			this.m_root = !!util.ssn(xn, "Root");
			this.m_headings = util.getDefs(xn, "Heading|Card", Heading);
			this.m_relationships = util.getDefs(xn, "Relationship", Relationship);
			this.useMatrixCard = util.getBooleanProperty(xn, "UseMatrixCard", true);

			if (xn && !this.m_relationships && this.m_root) {
				console.error("When using <Root /> with no <Relationship>, replace <Root /> with:");
				console.error("<Relationship Target=\"TYPE\">");
				console.error("    <AddPath Path=\"\" />");
				console.error("</Relationship>");
			}
		},

		resolve: function(/*Class*/rcRoot, /*Node*/nodeRoot, /*Boolean*/bConfigure) {
			var lmap = this.getLocaleMap(rcRoot, bConfigure);
			var bEdit = this.shouldAllowActions();
			var node = nodeRoot;
			var rc = rcRoot;

			// resolve relationships - relationships are relative to previous one
			this.m_relationships?.forEach((r, i) => {
				rc = r.resolve(lmap, rc, node);

				// if view is used as form for action, then do not allow row actions or attribute editing
				if (!bEdit) {
					r.resetActions();
				}

				// reset node since it only applies to first relationship
				if (i == 0) {
					node = null;
				}
			});

			// populate headings
			this.populateHeadingsFromCards(rcRoot);

			this.inherited(arguments);

			return lmap;
		},

		getPageRelationship() {
			return this.m_relationships?.[0];
		},

		getRelationships: function(/*Boolean?*/bConfigurable) {
			// summary: return all relationships in view, or only those with configurable attributes for display or filtering
			if (!this.m_relationships) return null;

			// filter out Relationship for spanner rows
			var rels = _.filter(this.m_relationships, function(r, i) {
				return !bConfigurable || !table.isTableSpannerRelationship(r);
			}, this);

			return rels;
		},

		/**
		 * @override
		 * Resolve cards relative to target class of first relationship, if one
		 */
		populateHeadingsFromCards(rcRoot, pathHints) {
			var rcTarget = rcRoot;
			var r = this.getPageRelationship();
			if (r) {
				rcTarget = r.getTargetClass(rcRoot);
				pathHints = r.pathHintsContext;
			}

			// if no headings, then add default Card to see if it can be resolved
			if (!this.m_headings && rcTarget) {
				this.m_headings = [
					_.extend(this.newHeading(), {
						m_bCard: true,
						m_id: def.getDefaultCardId(rcTarget.getType())
					})
				];
			}

			// check if <Type>.Matrix card exists, add the default matrix card
			if (this.m_headings && rcTarget && this.useMatrixCard) {
				let card = null;
				const cardId = def.getDefaultMatrixCardId(rcTarget.getType());
				def.getClassTypeDef(rcTarget, (t) => {
					if (card = t.getCard(cardId)) {
						card.m_idType = t.m_idType;
					}
					return card;
				});
				if (card) {
					this.m_headings.push(_.extend(this.newHeading(), {
						m_bCard: true,
						m_id: cardId
					}));
				}
			}

			this.inherited(arguments, [rcTarget, pathHints]);

			this.m_headings = this.m_headings.filter((h) => {
				// if a matrix heading, check to see if matrix class applies to the target class of the view
				const mp = h.getMatrixProcessor();
				return !mp || mp.isValidForClass(rcTarget);
			});
		},

		setFormData: function(fd) {
			// summary: remove actions from relationships if form data not editable
			this.inherited(arguments);

			if (fd && !fd.m_bEditable) {
				_.each(this.m_relationships, function(r) {
					r.resetActions();
				});
			}
		},

		shouldAllowActions: function() {
			const fd = this.getFormData();
			return (!fd || fd.m_bEditable) && (this.m_bAllowActions || !this.isReadOnly());
		},

		shouldResolveRootClass: function() {
			return !!this.m_root;
		}
	});
});
